import React, { useState, useMemo } from 'react';
import { useApp } from '../store';
import { UserRole, ProjectStage, Project, Priority } from '../types';
import { ProjectCard } from '../components/ProjectCard';
import { ProjectDetailModal } from '../components/ProjectDetailModal';
import { AddProjectModal } from '../components/AddProjectModal';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { 
  Trophy, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Plus,
  Filter,
  LayoutGrid,
  Layers,
  Search,
  Calendar as CalendarIcon,
  Columns,
  MessageSquare,
  Table as TableIcon,
  ArrowRight,
  Download,
  CalendarDays,
  XCircle,
  User as UserIcon
} from 'lucide-react';
import { 
  isSameDay, 
  format, 
  differenceInDays, 
  isAfter, 
  isWithinInterval, 
  endOfDay 
} from 'date-fns';
import { STAGE_CONFIG, PRIORITY_CONFIG } from '../constants';

const AdminOverview = () => {
  const { projects, getDevRankings, exportSystemData } = useApp();
  const rankings = getDevRankings();
  
  const stageData = useMemo(() => {
    const data: Record<string, number> = {
      'Upcoming': projects.filter(p => p.stage === ProjectStage.UPCOMING).length,
      'Design': projects.filter(p => p.stage === ProjectStage.DESIGN).length,
      'Development': projects.filter(p => p.stage === ProjectStage.DEVELOPMENT).length,
      'QA': projects.filter(p => p.stage === ProjectStage.QA).length,
      'Client Review': projects.filter(p => 
        p.stage === ProjectStage.SEND_TO_CLIENT || p.stage === ProjectStage.SENT_TO_CLIENT
      ).length,
    };

    return Object.entries(data).map(([name, count]) => ({ name, count }));
  }, [projects]);

  const overdueCount = projects.filter(p => p.isDelayed && p.stage !== ProjectStage.COMPLETED).length;

  return (
    <div className="space-y-8 animate-in slide-in-from-bottom-4 duration-500">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Active Pipeline', value: projects.filter(p => p.stage !== ProjectStage.COMPLETED).length, icon: <LayoutGrid className="text-blue-600" />, bg: 'bg-blue-50' },
          { label: 'In Production', value: projects.filter(p => p.stage === ProjectStage.DEVELOPMENT).length, icon: <Clock className="text-amber-600" />, bg: 'bg-amber-50' },
          { label: 'Overdue Risks', value: overdueCount, icon: <AlertCircle className="text-red-600" />, bg: 'bg-red-50' },
          { label: 'Total Finished', value: projects.filter(p => p.stage === ProjectStage.COMPLETED).length, icon: <CheckCircle className="text-emerald-600" />, bg: 'bg-emerald-50' },
        ].map((stat, i) => (
          <div key={i} className="bg-white p-7 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-5">
            <div className={`w-14 h-14 ${stat.bg} rounded-2xl flex items-center justify-center shadow-inner`}>
              {React.cloneElement(stat.icon as any, { className: 'w-7 h-7' })}
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">{stat.label}</p>
              <h4 className="text-3xl font-black text-slate-900 tracking-tight">{stat.value}</h4>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm relative">
          <div className="flex items-center justify-between mb-8">
             <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Active Workload distribution</h3>
             <button 
                onClick={exportSystemData}
                className="flex items-center gap-2 px-4 py-2 bg-slate-50 text-slate-600 hover:bg-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest border border-slate-200 transition-all"
             >
                <Download className="w-3.5 h-3.5" />
                Export Backup
             </button>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stageData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" fontSize={10} fontWeight="900" axisLine={false} tickLine={false} />
                <YAxis fontSize={10} fontWeight="900" axisLine={false} tickLine={false} />
                <Tooltip 
                  cursor={{fill: '#f8fafc'}}
                  contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}
                />
                <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                  {stageData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={['#6366f1', '#818cf8', '#60a5fa', '#f87171', '#c084fc'][index % 5]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Lead Performance</h3>
            <Trophy className="w-5 h-5 text-amber-500" />
          </div>
          <div className="flex-1 space-y-6 overflow-y-auto pr-2">
            {rankings.slice(0, 5).map((rank, i) => (
              <div key={rank.userId} className="flex items-center gap-4 group">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs ${i === 0 ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>
                  {i + 1}
                </div>
                <img src={`https://picsum.photos/seed/${rank.userId}/40/40`} className="w-10 h-10 rounded-full border-2 border-transparent group-hover:border-indigo-100 transition-all" alt="" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-black text-slate-900 truncate">{rank.userName}</p>
                  <p className="text-[10px] font-black text-indigo-50 uppercase tracking-widest">{rank.monthlyPoints} pts cycle</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-black text-slate-900">{rank.totalPoints}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const BoardCard: React.FC<{ project: Project; onClick: () => void; isAssignedToMe: boolean }> = ({ project, onClick, isAssignedToMe }) => {
  // Fix: Replace parseISO with native Date constructor
  const deadlineDate = project.currentDeadline ? new Date(project.currentDeadline) : new Date(project.overallDeadline);
  const today = new Date();
  const daysLeft = differenceInDays(deadlineDate, today);

  const getDeadlineColor = () => {
    if (daysLeft < 0) return 'text-red-600 bg-red-50';
    if (daysLeft <= 2) return 'text-orange-600 bg-orange-50';
    return 'text-slate-500';
  };

  return (
    <div 
      onClick={onClick}
      className={`bg-white p-5 rounded-2xl border ${isAssignedToMe ? 'border-indigo-600 ring-2 ring-indigo-50' : 'border-slate-200'} shadow-sm hover:border-indigo-400 hover:shadow-lg hover:shadow-indigo-50 cursor-pointer transition-all group relative`}
    >
      {isAssignedToMe && (
        <div className="absolute -top-3 left-4 px-2 py-0.5 bg-indigo-600 text-white rounded-lg text-[8px] font-black uppercase tracking-widest shadow-md">
           Assigned to me
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${PRIORITY_CONFIG[project.priority].color}`}>
          {project.priority}
        </span>
        {project.isDelayed && <AlertCircle className="w-3.5 h-3.5 text-red-500 animate-pulse" />}
      </div>
      <h4 className="text-sm font-black text-slate-900 mb-1 group-hover:text-indigo-600 transition-colors truncate">{project.name}</h4>
      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-4">{project.clientName}</p>
      
      <div className="flex items-center justify-between border-t border-slate-50 pt-4">
        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-black uppercase ${getDeadlineColor()}`}>
          <Clock className="w-3 h-3" />
          {format(deadlineDate, 'MMM d')}
        </div>
        <div className="flex items-center gap-3">
          {project.comments?.length > 0 && (
            <div className="flex items-center gap-1 text-slate-300">
              <MessageSquare className="w-3 h-3" />
              <span className="text-[10px] font-black">{project.comments.length}</span>
            </div>
          )}
          <div className="flex -space-x-2">
            {[project.assignedDesignerId, project.assignedDevManagerId].filter(Boolean).slice(0, 2).map(id => (
              <img key={id} src={`https://picsum.photos/seed/${id}/20/20`} className="w-6 h-6 rounded-full border-2 border-white shadow-sm" alt="Team member" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const KanbanBoard = ({ projects, onProjectClick, currentUserId }: { projects: Project[], onProjectClick: (p: Project) => void, currentUserId?: string }) => {
  const activeStages = Object.values(ProjectStage).filter(s => s !== ProjectStage.COMPLETED);

  return (
    <div className="flex gap-6 overflow-x-auto pb-6 h-full min-h-[500px]">
      {activeStages.map(stage => {
        const stageProjects = projects.filter(p => p.stage === stage);
        return (
          <div key={stage} className="min-w-[320px] w-[320px] flex flex-col bg-slate-100/30 rounded-[2.5rem] p-5 border border-slate-200/50">
            <div className="flex items-center justify-between mb-6 px-2">
              <div className="flex items-center gap-2.5">
                <span className={`w-2.5 h-2.5 rounded-full ${STAGE_CONFIG[stage].color.split(' ')[0]}`}></span>
                <h3 className="text-xs font-black text-slate-600 uppercase tracking-widest">{STAGE_CONFIG[stage].label}</h3>
              </div>
              <span className="text-[10px] font-black text-slate-400 bg-white px-3 py-1 rounded-full shadow-sm">{stageProjects.length}</span>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto min-h-0 pr-1">
              {stageProjects.map(project => (
                <BoardCard 
                  key={project.id} 
                  project={project} 
                  onClick={() => onProjectClick(project)} 
                  isAssignedToMe={project.assignedDesignerId === currentUserId || project.assignedDevManagerId === currentUserId || project.assignedQAId === currentUserId}
                />
              ))}
              {stageProjects.length === 0 && (
                <div className="border-2 border-dashed border-slate-200 rounded-3xl p-10 flex flex-col items-center justify-center opacity-30">
                  <Layers className="w-8 h-8 text-slate-300 mb-3" />
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">No Activity</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ProjectTable = ({ projects, onProjectClick, currentUserId }: { projects: Project[], onProjectClick: (p: Project) => void, currentUserId?: string }) => {
  return (
    <div className="bg-white border border-slate-200 rounded-[2rem] overflow-hidden shadow-sm">
      <table className="w-full text-left border-collapse">
        <thead className="bg-slate-50/50 border-b border-slate-200">
          <tr>
            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Project Name & Client</th>
            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Phase</th>
            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Priority</th>
            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Target Date</th>
            <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {projects.map(project => {
            const isAssignedToMe = project.assignedDesignerId === currentUserId || project.assignedDevManagerId === currentUserId || project.assignedQAId === currentUserId;
            return (
              <tr key={project.id} className={`hover:bg-slate-50 transition-colors group ${isAssignedToMe ? 'bg-indigo-50/30' : ''}`}>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-3">
                    {isAssignedToMe && <div className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse" title="Assigned to me" />}
                    <div>
                      <p className="font-black text-slate-900 group-hover:text-indigo-600 transition-colors tracking-tight">{project.name}</p>
                      <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-0.5">{project.clientName}</p>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-5">
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${STAGE_CONFIG[project.stage].color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${STAGE_CONFIG[project.stage].color.split(' ')[0]}`}></span>
                    {STAGE_CONFIG[project.stage].label}
                  </span>
                </td>
                <td className="px-8 py-5 text-center">
                  <span className={`inline-flex px-3 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${PRIORITY_CONFIG[project.priority].color}`}>
                    {project.priority}
                  </span>
                </td>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-2">
                    <Clock className={`w-3.5 h-3.5 ${project.isDelayed ? 'text-red-500' : 'text-slate-400'}`} />
                    <span className={`text-sm font-black tracking-tight ${project.isDelayed ? 'text-red-600' : 'text-slate-700'}`}>
                      {/* Fix: Replace parseISO with native Date constructor */}
                      {format(new Date(project.overallDeadline), 'MMM d, yyyy')}
                    </span>
                  </div>
                </td>
                <td className="px-8 py-5 text-right">
                  <button 
                    onClick={() => onProjectClick(project)}
                    className="p-3 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-2xl transition-all shadow-sm hover:shadow-indigo-100"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const RoleSpecificDashboard = () => {
  const { currentUser, projects, getDevRankings } = useApp();
  const rankings = getDevRankings();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showAddProject, setShowAddProject] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'board' | 'table'>('grid');
  
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('ALL');
  const [priorityFilter, setPriorityFilter] = useState<string>('ALL');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const filteredProjects = useMemo(() => {
    let list = projects.filter(p => p.stage !== ProjectStage.COMPLETED);

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || p.clientName.toLowerCase().includes(q));
    }
    if (stageFilter !== 'ALL') {
      list = list.filter(p => p.stage === stageFilter);
    }
    if (priorityFilter !== 'ALL') {
      list = list.filter(p => p.priority === priorityFilter);
    }
    
    if (customStartDate || customEndDate) {
      list = list.filter(p => {
        // Fix: Replace parseISO with native Date constructor
        const d = new Date(p.createdAt);
        const start = customStartDate ? new Date(new Date(customStartDate).setHours(0,0,0,0)) : null;
        const end = customEndDate ? endOfDay(new Date(customEndDate)) : null;
        
        if (start && end) {
           return isWithinInterval(d, { start, end });
        } else if (start) {
           return isAfter(d, start) || isSameDay(d, start);
        } else if (end) {
           return d <= end || isSameDay(d, end);
        }
        return true;
      });
    }

    return list;
  }, [projects, currentUser, search, stageFilter, priorityFilter, customStartDate, customEndDate]);

  const myStats = rankings.find(r => r.userId === currentUser?.id);

  const openProject = useMemo(() => {
    if (!selectedProject) return null;
    return projects.find(p => p.id === selectedProject.id) || null;
  }, [projects, selectedProject]);

  return (
    <div className="space-y-10 animate-in fade-in duration-500 pb-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-4xl font-black text-slate-900 tracking-tight">
            Hi, {currentUser?.name.split(' ')[0]} 👋
          </h2>
          <p className="text-slate-500 font-bold mt-1 uppercase tracking-widest text-xs italic">Global Agency Production Pipeline</p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="bg-white border border-slate-200 rounded-[1.25rem] p-1.5 flex gap-1 shadow-sm">
            <button 
              onClick={() => setViewMode('grid')}
              title="Grid Layout"
              className={`p-2.5 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setViewMode('board')}
              title="Board Layout"
              className={`p-2.5 rounded-xl transition-all ${viewMode === 'board' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <Columns className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setViewMode('table')}
              title="Table Layout"
              className={`p-2.5 rounded-xl transition-all ${viewMode === 'table' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <TableIcon className="w-5 h-5" />
            </button>
          </div>
          
          {currentUser?.role === UserRole.ADMIN && (
            <button 
              onClick={() => setShowAddProject(true)}
              className="flex items-center gap-3 px-8 py-4 bg-indigo-600 text-white font-black rounded-2xl shadow-xl shadow-indigo-100 hover:shadow-indigo-300 hover:-translate-y-1 transition-all uppercase tracking-widest text-xs"
            >
              <Plus className="w-5 h-5" />
              Create Project
            </button>
          )}
        </div>
      </div>

      <div className="bg-white p-5 rounded-[2rem] border border-slate-200 flex flex-wrap gap-5 items-center shadow-sm">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search Global Database..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all placeholder:font-medium shadow-inner"
          />
        </div>
        
        <div className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-2xl px-5 py-3 shadow-inner">
          <CalendarDays className="w-4.5 h-4.5 text-slate-400" />
          <div className="flex items-center gap-2">
            <input 
              type="date" 
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="bg-transparent border-none p-0 text-[10px] font-black uppercase focus:ring-0 focus:outline-none text-slate-700 w-[110px]"
            />
            <span className="text-[10px] font-black text-slate-300">TO</span>
            <input 
              type="date" 
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="bg-transparent border-none p-0 text-[10px] font-black uppercase focus:ring-0 focus:outline-none text-slate-700 w-[110px]"
            />
          </div>
          {(customStartDate || customEndDate) && (
            <button 
              onClick={() => { setCustomStartDate(''); setCustomEndDate(''); }}
              className="ml-2 text-slate-300 hover:text-red-500 transition-colors"
              title="Clear Date Range"
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Filter className="w-4.5 h-4.5 text-slate-400" />
          <select 
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="bg-slate-50 border border-slate-100 rounded-2xl px-5 py-3 text-xs font-black uppercase tracking-widest focus:outline-none appearance-none min-w-[140px] shadow-inner"
          >
            <option value="ALL">All Stages</option>
            {Object.values(ProjectStage).filter(s => s !== ProjectStage.COMPLETED).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <select 
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="bg-slate-50 border border-slate-100 rounded-2xl px-5 py-3 text-xs font-black uppercase tracking-widest focus:outline-none appearance-none min-w-[140px] shadow-inner"
          >
            <option value="ALL">All Priorities</option>
            {Object.values(Priority).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      {currentUser?.role === UserRole.ADMIN && <AdminOverview />}

      <div className="space-y-8">
        <div className="flex items-center justify-between border-b border-slate-100 pb-5">
          <div className="flex items-center gap-4">
            <h3 className="text-2xl font-black text-slate-900 tracking-tight uppercase italic">Active Workload</h3>
            <span className="bg-indigo-600 text-white px-3 py-1 rounded-full text-[10px] font-black shadow-lg shadow-indigo-100">{filteredProjects.length}</span>
          </div>
        </div>

        {filteredProjects.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-[3rem] p-24 text-center shadow-inner">
            <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-8">
              <Layers className="w-12 h-12 text-slate-200" />
            </div>
            <h4 className="text-2xl font-black text-slate-900 mb-3 tracking-tight uppercase italic">No Active Records Found</h4>
            <p className="text-slate-500 max-w-sm mx-auto font-medium text-lg leading-relaxed">Adjust filters or search terms to view agency production data.</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {filteredProjects.map(project => (
              <ProjectCard 
                key={project.id} 
                project={project} 
                onClick={() => setSelectedProject(project)} 
              />
            ))}
          </div>
        ) : viewMode === 'board' ? (
          <KanbanBoard projects={filteredProjects} onProjectClick={setSelectedProject} currentUserId={currentUser?.id} />
        ) : (
          <ProjectTable projects={filteredProjects} onProjectClick={setSelectedProject} currentUserId={currentUser?.id} />
        )}
      </div>

      {openProject && (
        <ProjectDetailModal 
          project={openProject} 
          onClose={() => setSelectedProject(null)} 
        />
      )}

      {showAddProject && (
        <AddProjectModal 
          onClose={() => setShowAddProject(false)} 
        />
      )}
    </div>
  );
};

export default RoleSpecificDashboard;